// ST-1706 -Claude- Recovery flow shell: email/phone code then reset password.

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card } from '@nearrme/ui-web';
import { forgotPassword, resetPassword } from '@nearrme/api-client';
import { getApiClient } from '../api/client';

type Step = 'request-code' | 'verify-code' | 'reset-password' | 'done';

export function ForgotPasswordPage() {
  const [step, setStep] = useState<Step>('request-code');
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (step === 'request-code') {
        await forgotPassword(getApiClient(), identifier);
        setStep('verify-code');
      } else if (step === 'verify-code') {
        setStep('reset-password');
      } else if (step === 'reset-password') {
        await resetPassword(getApiClient(), code, password);
        setStep('done');
      }
    } catch {
      setError('We could not process that request. Please check the details and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12 animate-fade-in">
      <Card padded={true}>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 text-center">
          Recover your account
        </h1>
        <p className="mt-2 text-center text-sm text-slate-600 dark:text-slate-400">
          Use your email or phone number to receive a secure code.
        </p>

        {step === 'done' ? (
          <div className="mt-6 text-center">
            <p className="text-slate-700 dark:text-slate-300">Your password has been reset.</p>
            <Link to="/login" className="mt-4 inline-block text-brand-700 dark:text-brand-300 font-medium">
              Back to login
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
                {error}
              </div>
            )}
            {step === 'request-code' && (
              <Field
                label="Email or phone"
                value={identifier}
                onChange={setIdentifier}
                placeholder="you@example.com or +91..."
              />
            )}
            {step === 'verify-code' && (
              <Field label="Verification code" value={code} onChange={setCode} placeholder="6-digit code" />
            )}
            {step === 'reset-password' && (
              <Field label="New password" type="password" value={password} onChange={setPassword} />
            )}
            <Button type="submit" variant="primary" size="lg" fullWidth disabled={isSubmitting}>
              {isSubmitting
                ? 'Please wait...'
                : step === 'request-code'
                  ? 'Send code'
                  : step === 'verify-code'
                    ? 'Verify code'
                    : 'Reset password'}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}

function Field(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{props.label}</span>
      <input
        type={props.type ?? 'text'}
        required
        value={props.value}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
        className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
      />
    </label>
  );
}
